import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';
import adminAnalyticsService from '../../../services/adminAnalyticsService';

const ContentModerationPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadModerationQueue();
  }, []);

  const loadModerationQueue = async () => {
    setLoading(true);
    const result = await adminAnalyticsService.getModerationQueue('pending');
    if (result.success && result.data.length > 0) {
      setReports(result.data.map(item => ({
        id: item.id,
        type: item.resource_type,
        reporterName: item.reporter?.full_name || 'Anonymous',
        reportedContent: item.description || `${item.resource_type} reported`,
        contentType: item.resource_type,
        vendorName: item.details?.vendor_name || 'N/A',
        description: item.reason || 'No description provided',
        status: item.status,
        priority: item.priority,
        date: item.created_at,
        reviewText: item.details?.content || ''
      })));
    }
    setLoading(false);
  };

  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedReports, setSelectedReports] = useState([]);

  /* Commented out - using real Supabase data now
  const mockReports = [
    {
      id: 1,
      type: 'inappropriate_content',
      reporterName: 'Priya Sharma',
      reportedContent: 'Review for Rajesh Photography',
      contentType: 'review',
      vendorName: 'Rajesh Photography Studio',
      description: 'Inappropriate language and offensive content in the review',
      status: 'pending',
      priority: 'high',
      date: '2024-03-15T10:30:00Z',
      reviewText: 'This photographer was terrible and unprofessional. Would not recommend to anyone.',
      evidence: ['screenshot1.jpg', 'screenshot2.jpg']
    },
    {
      id: 2,
      type: 'fake_review',
      reporterName: 'System Auto-detect',
      reportedContent: 'Multiple reviews from same IP',
      contentType: 'review',
      vendorName: 'Mehndi by Priya',
      description: 'Multiple positive reviews posted from the same IP address within 1 hour',
      status: 'under_review',
      priority: 'medium',
      date: '2024-03-14T15:20:00Z',
      reviewText: null,
      evidence: ['ip_log.txt']
    },
    {
      id: 3,
      type: 'spam',
      reporterName: 'Kavita Patel',
      reportedContent: 'Vendor profile description',
      contentType: 'profile',
      vendorName: 'Golden Mandap Decorators',
      description: 'Profile contains excessive promotional content and external links',
      status: 'resolved',
      priority: 'low',
      date: '2024-03-13T09:45:00Z',
      reviewText: null,
      evidence: []
    },
    {
      id: 4,
      type: 'harassment',
      reporterName: 'Sarah Johnson',
      reportedContent: 'Private message harassment',
      contentType: 'message',
      vendorName: 'DJ Bollywood Beats',
      description: 'Vendor sent inappropriate messages after booking inquiry',
      status: 'pending',
      priority: 'high',
      date: '2024-03-12T14:30:00Z',
      reviewText: null,
      evidence: ['message_screenshot.jpg']
    }
  ];
  */ // End of mock data comment

  // Note: These duplicate state variables below can be removed - using the ones defined above
  // const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');
  // const [filterType, setFilterType] = useState('all');
  // const [selectedReports, setSelectedReports] = useState([]);

  // contentModeration stats now computed from real data
  const contentModeration = {
    pendingReports: reports.filter(r => r.status === 'pending').length,
    underReview: reports.filter(r => r.status === 'under_review').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
    dismissed: reports.filter(r => r.status === 'dismissed').length
  };

  const filteredReports = reports?.filter(report => {
    const matchesStatus = filterStatus === 'all' || report?.status === filterStatus;
    const matchesType = filterType === 'all' || report?.type === filterType;
    return matchesStatus && matchesType;
  });

  const handleReportAction = (reportId, action) => {
    setReports(prev => prev?.map(report => {
      if (report?.id === reportId) {
        switch (action) {
          case 'approve':
            return { ...report, status: 'resolved' };
          case 'reject':
            return { ...report, status: 'dismissed' };
          case 'investigate':
            return { ...report, status: 'under_review' };
          default:
            return report;
        }
      }
      return report;
    }));
  };

  const handleBulkAction = (action) => {
    if (selectedReports?.length === 0) return;
    
    setReports(prev => prev?.map(report => {
      if (selectedReports?.includes(report?.id)) {
        switch (action) {
          case 'approve':
            return { ...report, status: 'resolved' };
          case 'reject':
            return { ...report, status: 'dismissed' };
          case 'investigate':
            return { ...report, status: 'under_review' };
          default:
            return report;
        }
      }
      return report;
    }));
    setSelectedReports([]);
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev => 
      prev?.includes(reportId) 
        ? prev?.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports?.length === filteredReports?.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(filteredReports?.map(report => report?.id));
    }
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'inappropriate_content':
        return 'AlertTriangle';
      case 'fake_review':
        return 'Shield';
      case 'spam':
        return 'Mail';
      case 'harassment':
        return 'UserX';
      default:
        return 'Flag';
    }
  };

  const getReportTypeLabel = (type) => {
    switch (type) {
      case 'inappropriate_content':
        return 'Inappropriate Content';
      case 'fake_review':
        return 'Fake Review';
      case 'spam':
        return 'Spam/Promotional';
      case 'harassment':
        return 'Harassment';
      default:
        return 'Other';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'under_review':
        return 'bg-blue-100 text-blue-600';
      case 'resolved':
        return 'bg-green-100 text-green-600';
      case 'dismissed':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          Content Moderation
        </h2>
        <Button variant="primary" iconName="Shield">
          Create Report
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="border-b border-border-light">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'reports', label: `Reports (${contentModeration?.totalReports})` },
              { id: 'pending', label: `Pending (${contentModeration?.pendingReviews})` },
              { id: 'analytics', label: 'Analytics' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Moderation Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-600 text-sm font-medium">Total Reports</p>
                      <p className="text-2xl font-bold text-red-900">
                        {contentModeration?.totalReports}
                      </p>
                    </div>
                    <Icon name="Flag" size={24} className="text-red-600" />
                  </div>
                  <p className="text-xs text-red-700 mt-2">
                    {contentModeration?.violationsFound} violations found
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-600 text-sm font-medium">Pending Reviews</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {contentModeration?.pendingReviews}
                      </p>
                    </div>
                    <Icon name="Clock" size={24} className="text-yellow-600" />
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">Awaiting review</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-600 text-sm font-medium">Resolved Reports</p>
                      <p className="text-2xl font-bold text-green-900">
                        {contentModeration?.resolvedReports}
                      </p>
                    </div>
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    {((contentModeration?.resolvedReports / contentModeration?.totalReports) * 100)?.toFixed(1)}% resolution rate
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-600 text-sm font-medium">Violations Found</p>
                      <p className="text-2xl font-bold text-orange-900">
                        {contentModeration?.violationsFound}
                      </p>
                    </div>
                    <Icon name="AlertTriangle" size={24} className="text-orange-600" />
                  </div>
                  <p className="text-xs text-orange-700 mt-2">Action required</p>
                </div>
              </div>

              {/* Report Types Distribution */}
              <div className="bg-white border border-border-light rounded-lg p-6">
                <h3 className="font-semibold text-lg text-text-primary mb-4">Report Types</h3>
                <div className="space-y-3">
                  {contentModeration?.reportTypes?.map((type, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Icon name={getReportTypeIcon(type?.type?.toLowerCase()?.replace(/[^a-z]/g, '_'))} size={16} className="text-text-muted" />
                        <span className="text-text-primary">{type?.type}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${(type?.count / Math.max(...contentModeration?.reportTypes?.map(t => t?.count))) * 100}%` }}
                          />
                        </div>
                        <span className="text-text-secondary text-sm">{type?.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {(activeTab === 'reports' || activeTab === 'pending') && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="all">All Types</option>
                  <option value="inappropriate_content">Inappropriate Content</option>
                  <option value="fake_review">Fake Review</option>
                  <option value="spam">Spam</option>
                  <option value="harassment">Harassment</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedReports?.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-primary-50 rounded-lg">
                  <span className="text-sm text-primary font-medium">
                    {selectedReports?.length} report(s) selected
                  </span>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('approve')}>
                    Resolve
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('investigate')}>
                    Investigate
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('reject')}>
                    Dismiss
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedReports([])}>
                    Clear
                  </Button>
                </div>
              )}

              {/* Reports Table */}
              <div className="bg-white border border-border-light rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface-50">
                    <tr>
                      <th className="p-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedReports?.length === filteredReports?.length && filteredReports?.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-border-light"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Report</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Type</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Priority</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Status</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Date</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {filteredReports?.map((report) => (
                      <tr key={report?.id} className="hover:bg-surface-50">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedReports?.includes(report?.id)}
                            onChange={() => handleSelectReport(report?.id)}
                            className="rounded border-border-light"
                          />
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-text-primary">{report?.reportedContent}</p>
                            <p className="text-sm text-text-secondary">
                              by {report?.reporterName} • {report?.vendorName}
                            </p>
                            <p className="text-xs text-text-muted mt-1">{report?.description}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name={getReportTypeIcon(report?.type)} size={16} className="text-text-muted" />
                            <span className="text-sm text-text-primary">
                              {getReportTypeLabel(report?.type)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-1">
                            <Icon name="AlertCircle" size={14} className={getPriorityColor(report?.priority)} />
                            <span className={`text-sm font-medium capitalize ${getPriorityColor(report?.priority)}`}>
                              {report?.priority}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                            {report?.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-text-secondary">
                          {formatDate(report?.date)}
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              onClick={() => console.log('View report:', report?.id)}
                            />
                            {report?.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="CheckCircle"
                                  onClick={() => handleReportAction(report?.id, 'approve')}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="X"
                                  onClick={() => handleReportAction(report?.id, 'reject')}
                                />
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="MoreHorizontal"
                              onClick={() => console.log('More actions for:', report?.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary">
                Moderation Analytics
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">Monthly Report Trends</h4>
                  <div className="space-y-3">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May']?.map((month, index) => (
                      <div key={month} className="flex items-center justify-between">
                        <span className="text-text-primary">{month} 2024</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-red-500 h-2 rounded-full" 
                              style={{ width: `${Math.random() * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">{Math.floor(Math.random() * 20)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">Resolution Metrics</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Average Resolution Time</span>
                      <span className="font-medium text-text-primary">2.4 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Resolution Rate</span>
                      <span className="font-medium text-text-primary">82.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">False Positive Rate</span>
                      <span className="font-medium text-text-primary">8.7%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Escalation Rate</span>
                      <span className="font-medium text-text-primary">12.1%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentModerationPanel;